import React from 'react';
import { motion } from 'framer-motion';
import { Check, Zap } from 'lucide-react';

const pricingTiers = [
  {
    name: 'Starter',
    price: 0,
    period: 'forever',
    description: 'Perfect for Getting Started',
    features: [
      '100 conversations per month',
      '1 AI assistant',
      'Web chat only',
      'Visual flow builder',
      'Basic analytics',
      'Community support',
    ],
    cta: 'Start Free',
    href: '/register',
    popular: false,
  },
  {
    name: 'Professional',
    price: 99,
    period: 'month',
    description: 'For Growing Teams',
    badge: 'Most Popular',
    features: [
      '10,000 conversations per month',
      '10 AI assistants',
      'All channels (SMS, Voice, Chat)',
      'Advanced analytics',
      'Priority support',
      'Custom branding',
      'API access',
      'Team collaboration',
    ],
    cta: 'Start 14-Day Trial',
    href: '/register?plan=professional',
    popular: true,
  },
  {
    name: 'Enterprise',
    price: null,
    period: 'custom',
    description: 'For Large Organizations',
    features: [
      'Unlimited conversations',
      'Unlimited assistants',
      'White-label solution',
      'Dedicated account manager',
      '99.99% SLA',
      'Custom integrations',
      'Advanced security (SSO)',
      'On-premise option',
    ],
    cta: 'Contact Sales',
    href: '/contact-sales',
    popular: false,
  },
];

export const Pricing: React.FC = () => {
  return (
    <section id="pricing" className="relative py-24">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Simple, <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Transparent</span> Pricing
          </h2>
          <p className="text-xl text-gray-400">Start free, scale as you grow—no hidden fees</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`relative group ${
                tier.popular ? 'md:-mt-4 md:mb-4' : ''
              }`}
            >
              {tier.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold rounded-full">
                  {tier.badge}
                </div>
              )}

              <div className={`relative h-full bg-gray-800/50 backdrop-blur-lg border rounded-2xl p-8 ${
                tier.popular ? 'border-purple-500' : 'border-gray-700'
              }`}>
                {tier.popular && (
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl pointer-events-none" />
                )}

                <div className="relative">
                  <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                  <p className="text-gray-400 mb-6">{tier.description}</p>

                  <div className="mb-6">
                    {tier.price !== null ? (
                      <>
                        <span className="text-5xl font-bold text-white">${tier.price}</span>
                        <span className="text-gray-400">/{tier.period}</span>
                      </>
                    ) : (
                      <span className="text-5xl font-bold text-white">Custom</span>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <motion.a
                    href={tier.href}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`block w-full text-center py-3 rounded-lg font-semibold transition-all ${
                      tier.popular
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/50'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      {tier.popular && <Zap size={18} />}
                      {tier.cta}
                    </span>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
